import { useAppSelector } from '../../hooks/reduxToolkit'
import type { MessageModel } from '../../reduxToolkit/conversationsSlice'
import {
  selectMessageDeliveryStatus,
  type MessageDeliveryStatus,
} from '../../reduxToolkit/conversationsSelectors'
import type { MessageChannel } from '../../../types/supabase'
import type { AttachmentModel } from '../../reduxToolkit/attachmentsSlice'
import { Attachment } from '../attachment'
import { MessageBody } from './MessageBody'
import { formatDateInTimeZone, parseDateInTimeZone } from '../../../lib/formatDateInTimeZone'

import './MessageBubble.scss'

interface MessageBubbleProps {
  message: MessageModel
  /**
   * Show a "Delivered" / "Read" receipt on this message.  Callers set this on
   * the member's most recent message only, so — as in iMessage — the receipt
   * marks how far the thread has got rather than repeating down every bubble.
   *
   * "Sending…" and "Not Delivered" ignore this flag: they say something about
   * one specific message, so they belong on whichever message they apply to.
   */
  showDeliveryReceipt?: boolean
}

interface DeliveryReceipt {
  icon: string
  label: string
  modifier: string
}

export function getMessageChannel(message: MessageModel): MessageChannel {
  // Use explicit channel if available
  if (message.channel) {
    return message.channel
  }
  // Fall back to inferring from addresses for backwards compatibility
  if (message.from_address || message.to_address) {
    return 'email'
  }
  return 'in-app'
}

/**
 * The iMessage-style status line for one of the member's own chat messages,
 * or undefined when there's nothing to say about it.
 *
 * The progression is "Sending…" → "Delivered" → "Read": the first two come
 * from the sync queue (has this row reached the server?), the last from Alice
 * stamping `read_at`.  Alice's own replies and inbound provider email never
 * carry a receipt — the member didn't send them.
 */
function getDeliveryReceipt(
  message: MessageModel,
  deliveryStatus: MessageDeliveryStatus,
  showDeliveryReceipt: boolean
): DeliveryReceipt | undefined {
  const isMemberChatMessage =
    message.direction === 'INBOUND' && getMessageChannel(message) === 'in-app'
  if (!isMemberChatMessage) {
    return undefined
  }

  if (deliveryStatus === 'failed') {
    return { icon: 'error_outline', label: 'Not Delivered', modifier: 'failed' }
  }
  if (deliveryStatus === 'sending') {
    return { icon: 'schedule', label: 'Sending…', modifier: 'sending' }
  }
  if (!showDeliveryReceipt) {
    return undefined
  }
  if (message.read_at) {
    return { icon: 'done_all', label: 'Read', modifier: 'read' }
  }
  return { icon: 'done', label: 'Delivered', modifier: 'delivered' }
}

export function MessageBubble({ message, showDeliveryReceipt = false }: MessageBubbleProps) {
  const isOutbound = message.direction === 'OUTBOUND'
  const isAlice = isOutbound // OUTBOUND messages are from Alice (system to user)
  const channel = getMessageChannel(message)
  const isEmail = channel === 'email'

  // Look up attachments linked to this message
  const messageAttachments = useAppSelector((state) =>
    state.attachments.attachments.filter(
      (a: AttachmentModel) => a.table_name === 'messages' && a.record_id === message.id
    )
  )

  const deliveryStatus = useAppSelector((state) => selectMessageDeliveryStatus(state, message))
  const receipt = getDeliveryReceipt(message, deliveryStatus, showDeliveryReceipt)

  return (
    <div
      className={`alice-message ${isAlice ? 'alice-message--alice' : 'alice-message--user'} ${isEmail ? 'alice-message--email' : 'alice-message--in-app'}`}
    >
      <div className="alice-message__bubble">
        <div className="alice-message__header">
          {isAlice && (
            <div className="alice-message__sender">
              <i className="material-icons-outlined">support_agent</i>
              Alice
            </div>
          )}
          <div className="alice-message__channel">
            <i className="material-icons-outlined">
              {isEmail ? 'email' : 'chat_bubble_outline'}
            </i>
            <span>{isEmail ? 'Email' : 'Chat'}</span>
          </div>
        </div>
        {isEmail && message.subject && (
          <div className="alice-message__subject">
            {message.subject}
          </div>
        )}
        <MessageBody
          className="alice-message__content"
          text={message.body_text || message.body_html || ''}
        />
        {messageAttachments.length > 0 && (
          <div className="alice-message__attachments">
            <div className="alice-message__attachments-label">
              <i className="material-icons-outlined">attach_file</i>
              Attachments
            </div>
            {messageAttachments.map((attachment) => (
              <Attachment key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}
        <div className="alice-message__footer">
          <div className="alice-message__time">
            {formatDateInTimeZone(parseDateInTimeZone(message.created_at), {
              format: 'h:mm a',
            })}
          </div>
          {isEmail && (
            <div className="alice-message__email-info">
              {isOutbound ? message.to_address : message.from_address}
            </div>
          )}
          {receipt && (
            <div
              className={`alice-message__receipt alice-message__receipt--${receipt.modifier}`}
              aria-live="polite"
            >
              <i className="material-icons-outlined">{receipt.icon}</i>
              <span>{receipt.label}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
